import {
  AppModuleCode,
  APP_MODULE_CODES,
  PROBLEM_LIFECYCLE_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
  ProblemLifecycle,
  TicketPriority,
  TicketStatus,
  TicketType,
  isTicketTypeEnabled,
  type TicketListQuery,
  type TicketLookups,
} from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { flattenCategories } from '@/products/helpdesk/features/tickets/category-options';
import { MultiSelectFilter } from '@/products/helpdesk/features/tickets/multi-select-filter';
import {
  extraTicketFilterActive,
  extraTicketFilterChips,
  isCriticalFilterActive,
  isMineFilterActive,
  isUnassignedFilterActive,
  isWaitingForCustomerFilterActive,
  toggleCriticalQuery,
  toggleMineQuery,
  toggleUnassignedQuery,
  toggleWaitingForCustomerQuery,
} from '@/products/helpdesk/features/tickets/tickets-extra-filters';
import { useModule } from '@/features/auth/use-module';
import { useMe } from '@/features/auth/use-me';
import { cn } from '@/lib/utils';

const ALL = 'all';
const UNASSIGNED = 'unassigned';

type TicketsFiltersProps = {
  query: TicketListQuery;
  lookups: TicketLookups | undefined;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onChange: (patch: Partial<TicketListQuery>) => void;
  variant?: 'full' | 'simple';
};

export function TicketsFilters({
  query,
  lookups,
  searchInput,
  onSearchInputChange,
  onChange,
  variant = 'full',
}: TicketsFiltersProps) {
  const copy = useVoiceCopy();
  const me = useMe();
  const incidentsOn = useModule(AppModuleCode.INCIDENTS);
  const problemsOn = useModule(AppModuleCode.PROBLEMS);
  const queuesOn = useModule(AppModuleCode.QUEUES);
  const categories = flattenCategories(lookups?.categories ?? []);
  const myId = me.data?.id;
  const assignmentValues = [...(query.unassigned ? [UNASSIGNED] : []), ...(query.assigneeId ?? [])];
  const extraActive = extraTicketFilterActive(query);
  const extraChips = extraTicketFilterChips(query, lookups);
  const [moreOpen, setMoreOpen] = useState(false);
  const extraOpen = extraActive || moreOpen;

  const searchField = (
    <div className="space-y-1.5 sm:col-span-2">
      <Label htmlFor="ticket-search">Suche</Label>
      <Input
        id="ticket-search"
        value={searchInput}
        onChange={(event) => onSearchInputChange(event.target.value)}
        placeholder={
          variant === 'simple'
            ? copy('helpdesk.ticket.search.prompt')
            : 'Betreff, Beschreibung oder #Nummer'
        }
      />
    </div>
  );

  const statusFilter = (
    <MultiSelectFilter
      id="ticket-status"
      label="Status"
      allLabel="Alle Status"
      values={query.status}
      options={Object.values(TicketStatus).map((status) => ({
        value: status,
        label: TICKET_STATUS_LABELS[status],
      }))}
      onChange={(status) => onChange({ status: status as TicketListQuery['status'], page: 1 })}
    />
  );

  if (variant === 'simple') {
    return (
      <div className="grid min-w-0 gap-3 sm:grid-cols-2">
        {searchField}
        {statusFilter}
      </div>
    );
  }

  const mineActive = isMineFilterActive(query, myId);
  const unassignedActive = isUnassignedFilterActive(query);
  const waitingActive = isWaitingForCustomerFilterActive(query);
  const criticalActive = isCriticalFilterActive(query);

  return (
    <div className="min-w-0 space-y-3">
      <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {searchField}
        {statusFilter}
        <MultiSelectFilter
          id="ticket-assignment"
          label="Zuweisung"
          allLabel="Alle Zuweisungen"
          values={assignmentValues}
          options={[
            ...(myId ? [{ value: myId, label: 'Meine' }] : []),
            { value: UNASSIGNED, label: 'Nicht zugewiesen' },
            ...(lookups?.agents ?? [])
              .filter((agent) => agent.id !== myId)
              .map((agent) => ({
                value: agent.id,
                label: agent.displayName,
              })),
          ]}
          onChange={(values) => {
            const next = values ?? [];
            const unassigned = next.includes(UNASSIGNED) ? true : undefined;
            const assigneeId = next.filter((value) => value !== UNASSIGNED);
            onChange({
              unassigned,
              assigneeId: assigneeId.length > 0 ? assigneeId : undefined,
              page: 1,
            });
          }}
        />
        <MultiSelectFilter
          id="ticket-priority"
          label="Priorität"
          allLabel="Alle Prioritäten"
          values={query.priority}
          options={Object.values(TicketPriority).map((priority) => ({
            value: priority,
            label: TICKET_PRIORITY_LABELS[priority],
          }))}
          onChange={(priority) =>
            onChange({ priority: priority as TicketListQuery['priority'], page: 1 })
          }
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {myId && (
          <QuickChip pressed={mineActive} onClick={() => onChange(toggleMineQuery(query, myId))}>
            Meine
          </QuickChip>
        )}
        <QuickChip
          pressed={unassignedActive}
          onClick={() => onChange(toggleUnassignedQuery(query))}
        >
          Nicht zugewiesen
        </QuickChip>
        <QuickChip
          pressed={waitingActive}
          onClick={() => onChange(toggleWaitingForCustomerQuery(query))}
        >
          Wartet auf Kunde
        </QuickChip>
        <QuickChip pressed={criticalActive} onClick={() => onChange(toggleCriticalQuery(query))}>
          Kritisch
        </QuickChip>
      </div>

      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2"
          aria-expanded={extraOpen}
          aria-controls="ticket-extra-filters"
          onClick={() => {
            if (!extraActive) {
              setMoreOpen((open) => !open);
            }
          }}
        >
          Weitere Filter
          <ChevronDownIcon
            className={cn('size-4 transition-transform', extraOpen && 'rotate-180')}
            aria-hidden
          />
        </Button>

        {extraChips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" aria-label="Aktive weitere Filter">
            {extraChips.map((chip) => (
              <Badge key={chip.id} variant="outline" asChild>
                <button type="button" onClick={() => onChange(chip.patch)}>
                  {chip.label}
                  <XIcon />
                  <span className="sr-only">Filter entfernen</span>
                </button>
              </Badge>
            ))}
          </div>
        )}

        {extraOpen && (
          <div
            id="ticket-extra-filters"
            className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6"
          >
            <MultiSelectFilter
              id="ticket-type"
              label="Typ"
              allLabel="Alle Typen"
              values={query.type}
              options={Object.values(TicketType)
                .filter((type) =>
                  isTicketTypeEnabled(type, me.data?.enabledModules ?? APP_MODULE_CODES),
                )
                .map((type) => ({
                  value: type,
                  label: TICKET_TYPE_LABELS[type],
                }))}
              onChange={(type) => onChange({ type: type as TicketListQuery['type'], page: 1 })}
            />
            {incidentsOn && (
              <div className="space-y-1.5">
                <Label htmlFor="ticket-major">Major Incident</Label>
                <Select
                  value={query.isMajor === true ? 'major' : ALL}
                  onValueChange={(value) =>
                    onChange({
                      isMajor: value === 'major' ? true : undefined,
                      page: 1,
                    })
                  }
                >
                  <SelectTrigger id="ticket-major" className="w-full">
                    <SelectValue placeholder="Alle Tickets" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Alle Tickets</SelectItem>
                    <SelectItem value="major">Nur Major Incidents</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {problemsOn && (
              <MultiSelectFilter
                id="ticket-problem-lifecycle"
                label="Problem-Status"
                allLabel="Alle Problem-Status"
                values={query.problemLifecycle}
                options={Object.values(ProblemLifecycle).map((lifecycle) => ({
                  value: lifecycle,
                  label: PROBLEM_LIFECYCLE_LABELS[lifecycle],
                }))}
                onChange={(problemLifecycle) =>
                  onChange({
                    problemLifecycle: problemLifecycle as TicketListQuery['problemLifecycle'],
                    page: 1,
                  })
                }
              />
            )}
            <MultiSelectFilter
              id="ticket-category"
              label="Kategorie"
              allLabel="Alle Kategorien"
              values={query.categoryId}
              options={categories.map((category) => ({
                value: category.id,
                label: category.label,
              }))}
              onChange={(categoryId) => onChange({ categoryId, page: 1 })}
            />
            {queuesOn && (
              <MultiSelectFilter
                id="ticket-queue"
                label="Warteschlange"
                allLabel="Alle Warteschlangen"
                values={query.queueId}
                options={(lookups?.queues ?? []).map((queue) => ({
                  value: queue.id,
                  label: queue.name,
                }))}
                onChange={(queueId) => onChange({ queueId, page: 1 })}
              />
            )}
            <div className="space-y-1.5">
              <Label htmlFor="ticket-from">Von</Label>
              <Input
                id="ticket-from"
                type="date"
                lang="de"
                aria-describedby="ticket-date-format-hint"
                value={query.createdFrom ?? ''}
                onChange={(event) =>
                  onChange({ createdFrom: event.target.value || undefined, page: 1 })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticket-to">Bis</Label>
              <Input
                id="ticket-to"
                type="date"
                lang="de"
                aria-describedby="ticket-date-format-hint"
                value={query.createdTo ?? ''}
                onChange={(event) =>
                  onChange({ createdTo: event.target.value || undefined, page: 1 })
                }
              />
            </div>
            <p id="ticket-date-format-hint" className="col-span-full text-xs text-muted-foreground">
              Zeitraum als Tag.Monat.Jahr (TT.MM.JJJJ).
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function QuickChip({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <Button
      type="button"
      size="xs"
      variant={pressed ? 'default' : 'outline'}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
